import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Save, RefreshCw, CheckCircle2, ToggleLeft, ToggleRight,
} from "lucide-react";

const EVENT_LABELS = {
  signup_started: "Signup started",
  signup_completed: "Signup completed",
  bill_payment_initiated: "BBPS bill payment initiated",
  bill_payment_completed: "BBPS bill payment completed",
  payout_initiated: "Payout initiated",
  payout_completed: "Payout completed",
  wallet_topup: "Wallet top-up",
  lead: "Landing page lead (CTA click)",
};

export default function AdminAnalytics() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ga4_measurement_id: "",
    fb_pixel_id: "",
    enabled: true,
    custom_events: {},
  });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics/config");
      setConfig(data.config);
      setForm(data.config);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load analytics config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/analytics/config", form);
      setConfig(data.config);
      setForm(data.config);
      toast.success(data.message || "Analytics config saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changed = config && JSON.stringify(form) !== JSON.stringify(config);

  const toggleEvent = (key) => {
    setForm((f) => ({
      ...f,
      custom_events: {
        ...f.custom_events,
        [key]: !f.custom_events[key],
      },
    }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto p-6 lg:p-10 space-y-8" data-testid="admin-analytics-page">
        <div>
          <p className="overline">Admin</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-1">
            Analytics & Tracking
          </h1>
          <p className="text-muted-foreground mt-2">
            Configure Google Analytics (GA4) and Facebook Pixel to track page views,
            conversions, and custom BBPS / Payout events. Scripts load automatically
            on every page once IDs are saved.
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <RefreshCw className="h-4 w-4 animate-spin" /> Loading...
          </div>
        ) : (
          <div className="space-y-8">
            {/* Master toggle */}
            <section className="rounded-xl border border-border p-6" data-testid="section-master-toggle">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-lg">Tracking enabled</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    When OFF, no GA4 or Pixel scripts are loaded on any page.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, enabled: !f.enabled }))}
                  className="text-primary"
                  data-testid="toggle-tracking-enabled"
                >
                  {form.enabled ? (
                    <ToggleRight className="h-8 w-8" strokeWidth={1.5} />
                  ) : (
                    <ToggleLeft className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                  )}
                </button>
              </div>
            </section>

            {/* GA4 */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-ga4">
              <div className="flex items-center gap-3">
                <BarChart3 className="h-5 w-5 text-primary" />
                <h2 className="font-semibold text-lg">Google Analytics 4</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Paste your GA4 Measurement ID. Page views are tracked automatically on every route change.
                Custom events fire on signup, bill payments, payouts, and wallet top-ups.
              </p>
              <div className="space-y-2">
                <Label htmlFor="ga4_id">Measurement ID</Label>
                <Input
                  id="ga4_id"
                  data-testid="input-ga4-id"
                  placeholder="G-XXXXXXXXXX"
                  value={form.ga4_measurement_id}
                  onChange={(e) => setForm({ ...form, ga4_measurement_id: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Find this at{" "}
                  <a href="https://analytics.google.com" target="_blank" rel="noreferrer" className="underline">
                    analytics.google.com
                  </a>{" "}
                  → Admin → Data Streams → your stream → Measurement ID.
                </p>
              </div>
            </section>

            {/* Facebook Pixel */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-fb-pixel">
              <div className="flex items-center gap-3">
                <svg className="h-5 w-5 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                <h2 className="font-semibold text-lg">Facebook Pixel</h2>
              </div>
              <p className="text-sm text-muted-foreground">
                Paste your Pixel ID. Standard events (<code>PageView</code>, <code>Purchase</code>,{" "}
                <code>CompleteRegistration</code>, <code>Lead</code>) fire automatically.
                Custom events map to Facebook standard events for optimal ad optimisation.
              </p>
              <div className="space-y-2">
                <Label htmlFor="fb_pixel_id">Pixel ID</Label>
                <Input
                  id="fb_pixel_id"
                  data-testid="input-fb-pixel-id"
                  placeholder="123456789012345"
                  value={form.fb_pixel_id}
                  onChange={(e) => setForm({ ...form, fb_pixel_id: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Find this at{" "}
                  <a href="https://business.facebook.com/events_manager" target="_blank" rel="noreferrer" className="underline">
                    Meta Events Manager
                  </a>{" "}
                  → Data Sources → your Pixel → Settings.
                </p>
              </div>
            </section>

            {/* Custom events toggles */}
            <section className="rounded-xl border border-border p-6 space-y-4" data-testid="section-custom-events">
              <h2 className="font-semibold text-lg">Custom event tracking</h2>
              <p className="text-sm text-muted-foreground">
                Toggle which business events fire to GA4 and Facebook Pixel.
                Each event maps to a Facebook standard event for ad optimisation.
              </p>
              <div className="grid gap-2">
                {Object.entries(EVENT_LABELS).map(([key, label]) => {
                  const on = form.custom_events?.[key] !== false;
                  return (
                    <div
                      key={key}
                      className="flex items-center justify-between px-3 py-2 rounded-lg border border-border hover:bg-accent/30 transition-colors"
                      data-testid={`event-toggle-${key}`}
                    >
                      <div>
                        <span className="text-sm font-medium">{label}</span>
                        <span className="ml-2 text-[10px] font-mono text-muted-foreground">{key}</span>
                      </div>
                      <button type="button" onClick={() => toggleEvent(key)} className="shrink-0">
                        {on ? (
                          <ToggleRight className="h-6 w-6 text-primary" strokeWidth={1.5} />
                        ) : (
                          <ToggleLeft className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* FB Pixel ↔ GA4 mapping reference */}
            <section className="rounded-xl border border-dashed border-primary/30 bg-primary/5 p-5" data-testid="section-event-mapping">
              <h3 className="font-semibold text-sm mb-2">Event mapping reference</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="py-1.5 pr-3 font-medium">App event</th>
                      <th className="py-1.5 px-3 font-medium">GA4 event</th>
                      <th className="py-1.5 pl-3 font-medium">FB Pixel event</th>
                    </tr>
                  </thead>
                  <tbody className="font-mono">
                    {[
                      ["signup_started", "signup_started", "InitiateCheckout"],
                      ["signup_completed", "signup_completed", "CompleteRegistration"],
                      ["bill_payment_initiated", "bill_payment_initiated", "InitiateCheckout"],
                      ["bill_payment_completed", "bill_payment_completed", "Purchase"],
                      ["payout_initiated", "payout_initiated", "InitiateCheckout"],
                      ["payout_completed", "payout_completed", "Purchase"],
                      ["wallet_topup", "wallet_topup", "AddPaymentInfo"],
                      ["lead", "lead", "Lead"],
                    ].map(([app, ga4, fb]) => (
                      <tr key={app} className="border-b border-border/40">
                        <td className="py-1.5 pr-3">{app}</td>
                        <td className="py-1.5 px-3 text-muted-foreground">{ga4}</td>
                        <td className="py-1.5 pl-3 text-blue-600">{fb}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Save */}
            <div className="flex items-center gap-4">
              <Button
                data-testid="save-analytics-config"
                onClick={save}
                disabled={saving || !changed}
                className="min-w-[140px]"
              >
                {saving ? (
                  <><RefreshCw className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : changed ? (
                  <><Save className="h-4 w-4 mr-2" /> Save changes</>
                ) : (
                  <><CheckCircle2 className="h-4 w-4 mr-2" /> Saved</>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
